import React, { useEffect, useState } from 'react';
import { AlertTriangle, ClipboardCheck } from 'lucide-react';
import { useAuth } from '../../lib/auth';

const API_PATH = '/adhoc-risk-assessments/pilot';

export default function PokolbinRiskAssessment({ onClose }) {
  const { api } = useAuth();

  const [data, setData] = useState(null);
  const [responses, setResponses] = useState({});
  const [observations, setObservations] = useState('');
  const [controlMeasures, setControlMeasures] = useState('');
  const [accessDecision, setAccessDecision] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [signature, setSignature] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    let active = true;

    api.get(API_PATH)
      .then(({ data: assessment }) => {
        if (active) setData(assessment);
      })
      .catch((err) => {
        if (active) {
          setError(err.response?.data?.detail || 'Unable to load assessment');
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, [api]);

  const questions = Object.entries(data?.questions || {});
  const allAnswered = questions.length > 0 &&
    questions.every(([key]) => responses[key]);

  const hasNegativeAnswer = Object.values(responses).includes('No');

  const requiresControls = hasNegativeAnswer ||
    accessDecision === 'do_not_proceed';

  const canSubmit = data?.can_submit &&
    allAnswered &&
    accessDecision &&
    acknowledged &&
    signature.trim().length > 0 &&
    (!requiresControls || controlMeasures.trim()) &&
    (accessDecision !== 'do_not_proceed' || observations.trim()) &&
    !(hasNegativeAnswer && accessDecision === 'safe_to_proceed') &&
    !saving;

  const submit = async () => {
    if (!canSubmit) return;

    setSaving(true);
    setError('');

    try {
      const response = await api.post(API_PATH, {
        responses,
        observations,
        control_measures: controlMeasures,
        access_decision: accessDecision,
        officer_acknowledgement: acknowledged,
        officer_signature: signature.trim(),
      });

      setResult(response.data);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
        'Assessment submission failed'
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="p-4 text-zinc-300">Loading assessment...</p>;
  }

  if (!data) {
    return <p className="p-4 text-red-400">{error}</p>;
  }

  const existing = data.submission;
  const finalResult = result || existing;

  return (
    <div className="space-y-5 text-zinc-100">
      <div>
        <h2 className="flex items-center gap-2 text-xl font-bold">
          <ClipboardCheck className="w-5 h-5 text-amber-400" />
          Site Access Risk Assessment
        </h2>
        <p className="text-sm text-zinc-400 mt-1">
          {data.job_number} · {data.client_ref}
        </p>
        <p className="text-sm text-zinc-400">
          {data.site_name} · {data.assigned_to_name}
        </p>
      </div>

      {finalResult ? (
        <div className="rounded border border-amber-700 bg-amber-950/30 p-4">
          <p className="font-semibold">
            Assessment recorded: {finalResult.status}
          </p>
          <p className="text-sm mt-2">
            Submission does not authorise site entry.
            Follow supervisor instructions and approved site controls.
          </p>
        </div>
      ) : (
        <>
          <div className="rounded border border-amber-800 bg-amber-950/20 p-3 text-sm">
            <AlertTriangle className="inline w-4 h-4 mr-2" />
            Assess conditions before entering the site.
            If access is unsafe, do not proceed and contact your supervisor.
          </div>

          {questions.map(([key, question]) => (
            <fieldset key={key} className="border-b border-zinc-800 pb-4">
              <legend className="font-medium mb-2">{question}</legend>
              <div className="flex flex-wrap gap-3">
                {['Yes', 'No', 'N/A']
                  .filter(answer => answer !== 'N/A' || ![
                    'four_wheel_drive',
                    'road_condition',
                    'communications',
                    'emergency_access',
                  ].includes(key))
                  .map(answer => (
                  <label key={answer} className="flex items-center gap-2">
                    <input
                      type="radio"
                      name={key}
                      value={answer}
                      checked={responses[key] === answer}
                      onChange={() => setResponses(prev => ({
                        ...prev,
                        [key]: answer,
                      }))}
                    />
                    {answer}
                  </label>
                ))}
              </div>
            </fieldset>
          ))}

          <label className="block space-y-2">
            <span className="font-medium">Observations and hazards</span>
            <textarea
              className="w-full rounded border border-zinc-700 bg-zinc-900 p-3"
              rows={4}
              value={observations}
              onChange={e => setObservations(e.target.value)}
              maxLength={5000}
            />
          </label>

          <label className="block space-y-2">
            <span className="font-medium">Control measures / escalation</span>
            <textarea
              className="w-full rounded border border-zinc-700 bg-zinc-900 p-3"
              rows={4}
              value={controlMeasures}
              onChange={e => setControlMeasures(e.target.value)}
              maxLength={5000}
            />
          </label>

          <fieldset className="space-y-3">
            <legend className="font-semibold">Access decision</legend>

            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="access_decision"
                value="safe_to_proceed"
                checked={accessDecision === 'safe_to_proceed'}
                disabled={hasNegativeAnswer}
                onChange={() => setAccessDecision('safe_to_proceed')}
              />
              Conditions assessed — Safe to Proceed
            </label>

            <label className="flex items-center gap-2 text-amber-300">
              <input
                type="radio"
                name="access_decision"
                value="do_not_proceed"
                checked={accessDecision === 'do_not_proceed'}
                onChange={() => setAccessDecision('do_not_proceed')}
              />
              Do Not Proceed — Supervisor Review Required
            </label>
          </fieldset>

          <label className="block space-y-2">
            <span className="font-medium">
              Officer electronic acknowledgement — type your full name
            </span>
            <input
              type="text"
              value={signature}
              onChange={e => setSignature(e.target.value)}
              maxLength={150}
              autoComplete="name"
              className="w-full rounded border border-zinc-700 bg-zinc-900 p-3"
              placeholder="Full name"
            />
          </label>

          <label className="flex items-start gap-3 text-sm">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={e => setAcknowledged(e.target.checked)}
            />
            <span>
              I confirm these responses reflect my assessment of the
              conditions observed. I understand that submitting this form
              does not constitute authorisation to enter an unsafe site.
            </span>
          </label>

          {error && <p className="text-red-400 text-sm">{error}</p>}

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              disabled={!canSubmit}
              onClick={submit}
              className="rounded bg-amber-600 px-4 py-3 font-semibold
                         text-white disabled:opacity-40"
            >
              {saving ? 'Submitting...' : 'Submit Assessment'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-zinc-600 px-4 py-3"
            >
              Close
            </button>
          </div>
        </>
      )}
    </div>
  );
}
